import { useContext, useEffect, useRef, useState } from 'react'
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom'

function useRouterBlock(when: boolean, onBlock: (retry: () => void) => void) {
  const { navigator } = useContext(NavigationContext)
  const onBlockRef = useRef(onBlock)
  onBlockRef.current = onBlock

  useEffect(() => {
    if (!when) return
    const unblock = (navigator as any).block((tx: any) => {
      onBlockRef.current(() => {
        unblock()
        tx.retry()
      })
    })
    return unblock
  }, [navigator, when])
}

export function useUnsavedChanges(
  isDirty: boolean,
  onSave: () => Promise<void>,
  { blockRouter = false }: { blockRouter?: boolean } = {}
) {
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  const [saving, setSaving] = useState(false)

  // Block browser reload / native back button
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // Block React Router SPA navigation (sidebar links, etc.)
  useRouterBlock(blockRouter && isDirty, (retry) => {
    setPendingAction(() => retry)
  })

  const guardNavigation = (action: () => void) => {
    if (!isDirty) {
      action()
      return
    }
    setPendingAction(() => action)
  }

  const handleLeave = () => {
    pendingAction?.()
    setPendingAction(null)
  }

  const handleCancel = () => {
    setPendingAction(null)
  }

  const handleSaveAndContinue = async () => {
    setSaving(true)
    try {
      await onSave()
      pendingAction?.()
      setPendingAction(null)
    } catch {
      // save failed — modal stays open so user can retry or leave
    } finally {
      setSaving(false)
    }
  }

  return {
    guardNavigation,
    isModalOpen: pendingAction !== null,
    handleLeave,
    handleCancel,
    handleSaveAndContinue,
    saving,
  }
}
